import { useEffect, useRef } from "react";
import { Check, Monitor, Moon, Sun } from "lucide-react";
import { AVAILABLE_THEMES, useVisualTheme } from "../lib/themeLoader";
import { useThemeMode, type ThemeMode } from "../hooks/useTheme";
import { ThemeMiniature } from "./ThemeMiniature";

/** Inner content shared by the sidebar dropdown and the Settings page
 *  inline panel: the swatch grid + the Light / Dark / System row.
 *  Owns the live wiring; the wrapping surface (popover or static panel)
 *  decides chrome and positioning. */
export function ThemePickerPanel() {
  const { theme: visualTheme, setTheme: setVisualTheme } = useVisualTheme();
  const { mode, setMode } = useThemeMode();

  return (
    <div className="flex flex-col">
      <div className="px-3 pt-2.5 pb-1.5">
        <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
          Theme
        </div>
      </div>

      <div className="px-2.5 pb-2 max-h-[340px] overflow-y-auto">
        <div className="grid grid-cols-2 gap-1.5">
          {AVAILABLE_THEMES.map((t) => {
            const active = visualTheme === t.id;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => setVisualTheme(t.id)}
                aria-pressed={active}
                title={t.description}
                className={`group relative flex flex-col gap-1 p-1.5 rounded-lg border transition-all text-left ${
                  active
                    ? "border-sky-500/80 bg-sky-50/60 dark:bg-sky-500/10"
                    : "border-transparent hover:border-slate-300 dark:hover:border-slate-600/70 hover:bg-slate-100/60 dark:hover:bg-slate-800/50"
                }`}
              >
                <div className="relative">
                  <ThemeMiniature themeId={t.id} />
                  {active && (
                    <div className="absolute top-1 right-1 w-4 h-4 rounded-full bg-sky-500 text-white flex items-center justify-center shadow ring-2 ring-white dark:ring-slate-900">
                      <Check className="w-2.5 h-2.5" strokeWidth={3.5} />
                    </div>
                  )}
                </div>
                <div className="text-[10.5px] font-medium leading-tight text-slate-700 dark:text-slate-300 truncate px-0.5">
                  {t.name}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <div className="border-t border-slate-200/60 dark:border-slate-700/50 px-3 py-2.5 flex items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
            Appearance
          </div>
          <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5 truncate">
            {mode === "system" ? "Follows your system" : `Always ${mode}`}
          </div>
        </div>
        <ModePills mode={mode} setMode={setMode} />
      </div>
    </div>
  );
}

function ModePills({
  mode,
  setMode,
}: {
  mode: ThemeMode;
  setMode: (m: ThemeMode) => void;
}) {
  const opts: { id: ThemeMode; label: string; Icon: typeof Sun }[] = [
    { id: "light", label: "Light", Icon: Sun },
    { id: "dark", label: "Dark", Icon: Moon },
    { id: "system", label: "System", Icon: Monitor },
  ];
  return (
    <div
      className="flex-shrink-0 inline-flex items-center p-0.5 rounded-lg bg-slate-200/70 dark:bg-slate-700/60"
      role="radiogroup"
      aria-label="Appearance mode"
    >
      {opts.map(({ id, label, Icon }) => {
        const active = mode === id;
        return (
          <button
            key={id}
            type="button"
            onClick={() => setMode(id)}
            role="radio"
            aria-checked={active}
            aria-label={label}
            title={label}
            className={`inline-flex items-center justify-center w-7 h-6 rounded-md transition-colors ${
              active
                ? "bg-sky-500 text-white shadow-sm"
                : "text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-slate-100"
            }`}
          >
            <Icon className="w-3.5 h-3.5" />
          </button>
        );
      })}
    </div>
  );
}

interface DropdownProps {
  open: boolean;
  onClose: () => void;
  /** Where the popover sits relative to the trigger. "right" anchors
   *  to the right of the trigger (sidebar bottom-section pattern);
   *  "top" stacks above (mobile or other compact callers). */
  anchor?: "right" | "top";
  /** Element to ignore for click-outside (typically the trigger button)
   *  so a click on the trigger toggles open/close cleanly without the
   *  outside-click handler immediately re-closing it. */
  ignoreRef?: React.RefObject<HTMLElement | null>;
}

/** Anchored popover wrapper around `ThemePickerPanel`. Closes on Esc
 *  and on click outside its own surface (and the trigger element, if
 *  one is passed via `ignoreRef`). Click on a swatch or pill applies
 *  immediately and leaves the popover open so the user can compare. */
export function ThemePickerDropdown({ open, onClose, anchor = "right", ignoreRef }: DropdownProps) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      const node = ref.current;
      const ignore = ignoreRef?.current;
      const target = e.target as Node;
      if (node && !node.contains(target) && (!ignore || !ignore.contains(target))) {
        onClose();
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    // Defer one tick so the click that opened the popover doesn't
    // immediately register as an outside click.
    const t = setTimeout(() => document.addEventListener("mousedown", onDocClick), 0);
    document.addEventListener("keydown", onKey);
    return () => {
      clearTimeout(t);
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, onClose, ignoreRef]);

  if (!open) return null;

  const positioning =
    anchor === "top"
      ? "bottom-full mb-2 left-0 origin-bottom-left"
      : "left-full ml-2 bottom-0 origin-bottom-left";

  return (
    <div
      ref={ref}
      role="menu"
      aria-label="Appearance"
      className={`absolute ${positioning} z-50 w-[280px] rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-xl shadow-slate-300/40 dark:shadow-black/50 overflow-hidden animate-popoverIn motion-reduce:animate-none`}
    >
      <ThemePickerPanel />
    </div>
  );
}
